import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = { class: "text-subtitle2 ellipsis" }
const _hoisted_3 = { class: "sfx-card__top-right-label" }
const _hoisted_4 = { class: "text-h4" }

import SfxCardCondensed from "src/components/card/SfxCardCondensed.vue"
import SfxChip from "src/components/data-point/SfxChip.vue"
import SfxDataPoint from "src/components/data-point/SfxDataPoint.vue"
import { useCacheStore } from "src/stores/cacheStore"
import { getAttachmentContent, getFirstImageAttachment } from "src/util/functions/attachments"
import {
  getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue,
  getIssueCardIcons,
  getIssueCardTime,
} from "src/util/functions/issues"
import { getWorkOrderCorrectiveItemData } from "src/util/functions/workOrder"
import { getWorkOrderPriorityData } from "src/util/functions/workOrderPriority"
import { IconRowElement } from "src/util/types/components/sfxIconRow"
import { IssueCardData } from "src/util/types/dataModels/issue"
import { CorrectiveWorkOnItem } from "src/util/types/dataModels/performedWork"
import { computed, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxCardIssueCondensed',
  props: {
  issue: {
    type: Object as PropType<IssueCardData>,
    required: true,
  },

  highlight: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { t } = useI18n()
const cacheStore = useCacheStore()

const timeSinceRaised = computed(() => getIssueCardTime(props.issue))
const storeIssue = ref<IssueCardData>()

const overdue = computed(() => (props.issue.overdueTime || 0) > 0)
const timeLabel = computed(() =>
  overdue.value ? t("sfxCard.issue_label_deadline") : t("sfxCard.issue_label_raised")
)
const chipOverdueLabel = t("time.generic_overdue")
const workDone = computed(() => props.issue.correctiveWork?.conclusion === "accepted")
const icons = ref<ReadonlyArray<IconRowElement>>([])

const workDomainDesignation = computed(() => workDomainRaw.value?.designation)
const issueThumbnail = ref<string | undefined>()

const issueThumbnailRaw = ref(props.issue.thumbnail)
const itemRaw = ref(props.issue.actionableItem)
const workDomainRaw = ref(props.issue.workDomainData)

const correctiveWorkOnItemForIssue = ref<CorrectiveWorkOnItem | undefined>()

const classes = computed(() => {
  return {
    "sfx-card-issue__overdue": overdue.value,
    "sfx-card-highlight": props.highlight,
  }
})

//endregion

//region Functions

async function assignData(issue: IssueCardData) {
  resetData()

  storeIssue.value = cacheStore.issueCard.get(issue.issueData.id)

  correctiveWorkOnItemForIssue.value = await getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(
    issue.issueData.id
  )

  assignItem(issue)
  assignWorkDomain(issue)
  await assignThumbnailAttachment(issue)
  icons.value = await getIcons(issue)
}

function resetData() {
  issueThumbnailRaw.value = undefined
  itemRaw.value = undefined
  workDomainRaw.value = undefined
}

function assignItem(issue: IssueCardData) {
  // We already have the data so skip it
  if (itemRaw.value) return

  const item = cacheStore.item.get(issue.issueData.actionable_item)

  itemRaw.value = item

  if (storeIssue.value) storeIssue.value.actionableItem = item
}

function assignWorkDomain(issue: IssueCardData) {
  // We already have the data so skip it
  if (workDomainRaw.value) return

  const workDomain = cacheStore.work_domain_workspace.get(issue.issueData.work_domain)

  workDomainRaw.value = workDomain

  if (storeIssue.value) storeIssue.value.workDomainData = workDomain
}

async function assignThumbnailAttachment(issue: IssueCardData) {
  // We already have the data so skip it
  if (issueThumbnailRaw.value !== undefined) return

  if (issue.issueData.attachments.length > 0) {
    const thumbnail = await getFirstImageAttachment(issue.issueData.attachments)

    if (thumbnail) {
      issueThumbnailRaw.value = thumbnail

      if (storeIssue.value) storeIssue.value.thumbnail = thumbnail
    }
  }
}

async function getIcons(issue: IssueCardData) {
  const issuePriority = issue.priorityData
  const correctiveWork = correctiveWorkOnItemForIssue.value
  let workOrder
  if (issue.issueData.corrective_work_order) {
    workOrder = await getWorkOrderCorrectiveItemData(issue.issueData.corrective_work_order)
  }
  let workOrderPriority
  if (workOrder) {
    workOrderPriority = await getWorkOrderPriorityData(workOrder?.priority)
  }
  return getIssueCardIcons(issuePriority, workOrderPriority, correctiveWork)
}

//endregion

//region Setup Code

watch(
  () => issueThumbnailRaw.value,
  async (thumbnail) => {
    issueThumbnail.value = thumbnail
      ? await getAttachmentContent(thumbnail, "issue_item", props.issue?.issueData.id)
      : undefined
  },
  { immediate: true }
)

watch(
  () => props.issue,
  async (newIssue) => {
    await assignData(newIssue)
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_space = _resolveComponent("q-space")!
  const _directive_truncate_middle = _resolveDirective("truncate-middle")!

  return (_openBlock(), _createBlock(SfxCardCondensed, {
    class: _normalizeClass(classes.value),
    "img-src": issueThumbnail.value,
    "icon-row": icons.value,
    done: workDone.value,
    hasImg: ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(workDomainDesignation.value), 1),
        (overdue.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(_component_q_space),
              _createElementVNode("span", _hoisted_3, [
                _createVNode(SfxChip, {
                  label: _unref(chipOverdueLabel),
                  "icon-right": "warning",
                  color: "negative",
                  "text-color": "white"
                }, null, 8, ["label"])
              ])
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_4, null, 512), [
        [_directive_truncate_middle, props.issue?.issueData.title]
      ]),
      _createVNode(SfxDataPoint, {
        label: timeLabel.value,
        "card-field": ""
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(timeSinceRaised.value), 1)
        ]),
        _: 1
      }, 8, ["label"])
    ]),
    _: 1
  }, 8, ["class", "img-src", "icon-row", "done"]))
}
}

})